/**
 * useExistingDebtInputs - Business Logic Hook
 *
 * Extracts all calculation and state management logic from ExistingDebtInputs component.
 * This hook provides a clean, testable API for managing existing debt inputs.
 *
 * Pattern Benefits:
 * - Testing: Logic can be tested independently from UI
 * - Separation: Component focuses on rendering, hook handles business logic
 * - Maintainability: Reduces component size and complexity
 *
 * Architecture Note (2025-11-25):
 * This hook handles multiple concerns (keyboard navigation, payment schedule generation,
 * reverse amortization, validation, fiscal year totals) in ~880 lines. This was reviewed
 * for potential splitting but the current state is acceptable. The complexity is inherent
 * to the domain—existing debt input is genuinely complex. The organization is reasonable
 * with a clean return structure: { handlers, computed, state, refs, constants }.
 *
 * Recommendation: Monitor during maintenance. If bugs become hard to trace or features
 * hard to add, consider splitting into usePaymentNavigation, useReverseAmortization,
 * and usePaymentValidation hooks.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ExistingDebtBuilder } from '~/builders/existing-debt/ExistingDebtBuilder';
import { toaster } from '~/components/ui/toaster';
import {
  EXISTING_DEBT_CALCULATIONS,
  EXISTING_DEBT_UI,
  EXISTING_DEBT_VALIDATION,
  getMonthFromDateString,
} from '~/config/existing-debt-constants';
import { roundInterestToNearestCent } from '~/engine/processing/calculators/core/financial-math/rounding';
import type {
  DateInputsForm,
  DateRecord,
  ExistingDebtInput,
  ExistingDebtInputForm,
  ExistingDebtPaymentForm,
  ExistingDebtPaymentInput,
} from '~/types';
import { calculateReverseAmortization } from '~/utils/reverseAmortization';

interface UseExistingDebtInputsProps {
  values: ExistingDebtInputForm;
  onUpdate: (values: ExistingDebtInputForm) => void;
  availablePaymentDates: DateRecord[];
  dateInputs?: DateInputsForm;
  showValidation?: boolean;
  isDisabled?: boolean;
}

export const useExistingDebtInputs = ({
  values,
  onUpdate,
  availablePaymentDates,
  dateInputs,
  showValidation = false,
  isDisabled = false,
}: UseExistingDebtInputsProps) => {
  // State Management
  const [finalPaymentDate, setFinalPaymentDate] = useState<string>('');
  const [paymentDateSearchQuery, setPaymentDateSearchQuery] = useState<string>('');
  const [autoMultiplyEnabled, setAutoMultiplyEnabled] = useState<boolean>(
    EXISTING_DEBT_UI.DEFAULT_AUTO_MULTIPLY_ENABLED
  );
  const [lockMayDates, setLockMayDates] = useState<boolean>(
    EXISTING_DEBT_UI.DEFAULT_LOCK_MAY_DATES
  );
  const [lockNovemberDates, setLockNovemberDates] = useState<boolean>(
    EXISTING_DEBT_UI.DEFAULT_LOCK_NOVEMBER_DATES
  );
  const [calculatedInterestPayments, setCalculatedInterestPayments] = useState<Map<number, number>>(
    new Map()
  );
  const [hasCalculatedReverseAmortization, setHasCalculatedReverseAmortization] =
    useState<boolean>(false);
  const [showClearAllDialog, setShowClearAllDialog] = useState<boolean>(false);

  // Track which fields have been touched (for validation UX)
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Mark field as touched
  const markTouched = useCallback((field: string) => {
    setTouched((prev) => new Set(prev).add(field));
  }, []);

  // Refs
  const inputRefs = useRef<Map<string, React.RefObject<HTMLInputElement>>>(new Map());
  const previousValuesRef = useRef<ExistingDebtInputForm | null>(null);
  const lastCalculatedProjectIdRef = useRef<string | undefined>(undefined);

  // Focus tracking: tracks the currently focused input to restore after re-renders
  const activeFocusRef = useRef<{ rowIndex: number; colIndex: number } | null>(null);
  const isNavigatingRef = useRef<boolean>(false); // Prevents auto-select conflicts during navigation

  // Helper: Perform reverse amortization calculation using extracted utility
  const performReverseAmortization = useCallback((payments: ExistingDebtPaymentForm[]) => {
    return calculateReverseAmortization(payments);
  }, []);

  // Effect 1: Initialize previousValuesRef on first mount
  // This ref tracks the previous values to detect meaningful changes
  //
  // Intentionally runs only once - we don't want to reset this ref
  // Suppression needed: We only want initialization to happen once, not when values change
  useEffect(() => {
    if (previousValuesRef.current === null) {
      previousValuesRef.current = values;
      if (values.id) {
        lastCalculatedProjectIdRef.current = values.id;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally empty: Only run on mount, not when values change
  }, [values]);

  // Effect 2: Detect tab switches (project ID changes)
  // Detect when user switches between different debt projects (tabs)
  // Clear calculations when switching to a different project
  // This prevents showing stale calculations from the previous project
  useEffect(() => {
    if (values.id && values.id !== lastCalculatedProjectIdRef.current) {
      setCalculatedInterestPayments(new Map());
      setHasCalculatedReverseAmortization(false);
      lastCalculatedProjectIdRef.current = values.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Only depend on values.id - setters are stable
  }, [values.id]);

  // Effect 3: Update previousValuesRef (every render)
  // Keep previousValuesRef in sync with current values
  // This allows other code to detect when values have changed
  //
  // This is a tracking effect, not a side effect
  // It needs to run on every render to stay synchronized
  useEffect(() => {
    if (previousValuesRef.current !== null) {
      previousValuesRef.current = values;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Only depend on values - we want this to run whenever values change
  }, [values]);

  /**
   * Calculate reverse amortization for all payments
   *
   * Reverse amortization works backwards from the final payment,
   * accumulating interest obligations from future payments.
   *
   * Process:
   * 1. Start at final payment (index N-1)
   * 2. Calculate Principal Interest for this payment
   * 3. Add Interest Payment from next payment (already calculated)
   * 4. Store result and move to previous payment
   * 5. Repeat until reaching first payment
   *
   * This ensures each payment knows its total interest obligation,
   * including all future interest that must be carried forward.
   *
   * Updates both:
   * - calculatedInterestPayments (Map for display)
   * - values.payments[].interestPayment (for persistence)
   */
  const calculateReverseAmortizationLocal = useCallback(() => {
    if (values.payments.length === 0) return;

    // Use shared helper to perform calculation
    const calculatedPayments = performReverseAmortization(values.payments);

    setCalculatedInterestPayments(calculatedPayments);
    setHasCalculatedReverseAmortization(true);

    // Save calculated interest payments back to the form so they persist
    // This ensures the calculation is available when processing cumulative debt
    // Round to nearest cent before storing as string
    const updatedPayments = values.payments.map((payment, index) => {
      const calculatedInterest = calculatedPayments.get(index);
      if (calculatedInterest !== undefined) {
        const roundedInterest = roundInterestToNearestCent(calculatedInterest);
        return {
          ...payment,
          interestPayment: roundedInterest.toFixed(2),
        };
      }
      return payment;
    });

    // Only update if there are actual changes to avoid infinite loops
    const hasChanges = updatedPayments.some((payment, index) => {
      const currentInterest = parseFloat(payment.interestPayment) || 0;
      const calculatedInterest = calculatedPayments.get(index) || 0;
      return (
        Math.abs(currentInterest - calculatedInterest) >
        EXISTING_DEBT_CALCULATIONS.FLOAT_COMPARISON_TOLERANCE
      );
    });

    if (hasChanges) {
      onUpdate({
        ...values,
        payments: updatedPayments,
      });
    }
  }, [values, performReverseAmortization, onUpdate]);

  // Effect 4: Auto-calculate reverse amortization (on payment changes)
  // Auto-calculate reverse amortization when payment data changes
  // This provides immediate feedback as users enter data
  //
  // Triggers when:
  // - Payments array changes (new payment added, payment removed)
  // - Any payment's principal or interest rate changes
  //
  // Only calculates if we have valid data (at least one payment with principal > 0 and rate > 0)
  //
  // Note: No timeout for immediate consistency
  // Previous implementation had timeout which caused race conditions
  useEffect(() => {
    // Early exit: No payments to calculate
    if (values.payments.length === 0) {
      return;
    }

    // Check if we have at least one payment with valid data
    const hasValidData = values.payments.some((payment) => {
      const principalStr = payment.principalPayment || '';
      const principal = parseFloat(principalStr.replace(/[^\d.-]/g, '')) || 0;
      const rateStr = payment.interestRate || '';
      const rate = parseFloat(rateStr) || 0;
      return principal > 0 && rate > 0;
    });

    // Early exit: No valid data to calculate
    if (!hasValidData) {
      return;
    }

    // Perform calculation immediately (no timeout)
    calculateReverseAmortizationLocal();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Only depend on values.payments - calculateReverseAmortizationLocal is a stable callback
    // We don't want to recalculate when other parts of values change
  }, [values.payments, calculateReverseAmortizationLocal]);

  // Helper function to check if a payment date should be disabled based on locked months
  // Parses month directly from "YYYY-MM-DD" string to avoid timezone issues
  const isPaymentDateLocked = useCallback(
    (paymentDate: string): boolean => {
      if (!paymentDate) return false;
      const month = getMonthFromDateString(paymentDate);

      if (lockMayDates && month === EXISTING_DEBT_UI.MONTHS.MAY) return true;
      if (lockNovemberDates && month === EXISTING_DEBT_UI.MONTHS.NOVEMBER) return true;
      return false;
    },
    [lockMayDates, lockNovemberDates]
  );

  // Helper to get or create ref for an input field
  const getInputRef = useCallback((rowIndex: number, colIndex: number) => {
    const key = `${rowIndex}-${colIndex}`;
    if (!inputRefs.current.has(key)) {
      inputRefs.current.set(
        key,
        React.createRef<HTMLInputElement>() as React.RefObject<HTMLInputElement>
      );
    }
    return inputRefs.current.get(key)!;
  }, []);

  // Track when an input receives focus (called by input components)
  const handleInputFocus = useCallback((rowIndex: number, colIndex: number) => {
    activeFocusRef.current = { rowIndex, colIndex };
  }, []);

  // Track when an input loses focus (called by input components)
  const handleInputBlur = useCallback(() => {
    // Only clear if we're not navigating (navigation will set new focus)
    if (!isNavigatingRef.current) {
      activeFocusRef.current = null;
    }
  }, []);

  // Check if we're currently in a programmatic navigation (for input components)
  const isNavigating = useCallback(() => {
    return isNavigatingRef.current;
  }, []);

  // Restore focus to the tracked input (useful after re-renders)
  const restoreFocus = useCallback(() => {
    if (activeFocusRef.current) {
      const { rowIndex, colIndex } = activeFocusRef.current;
      const ref = getInputRef(rowIndex, colIndex);
      requestAnimationFrame(() => {
        ref.current?.focus();
      });
    }
  }, [getInputRef]);

  // Navigate to next/previous field based on direction
  const navigateField = useCallback(
    (
      currentRowIndex: number,
      currentColIndex: number,
      direction: 'next' | 'prev' | 'up' | 'down' | 'left' | 'right'
    ) => {
      let nextRowIndex = currentRowIndex;
      let nextColIndex = currentColIndex;
      let attempts = 0;
      const maxAttempts = values.payments.length * 2; // Prevent infinite loops

      // Keep trying to find a non-disabled field
      while (attempts < maxAttempts) {
        attempts++;

        if (direction === 'next' || direction === 'right') {
          // Move to next column, or next row if at last column
          if (nextColIndex === EXISTING_DEBT_UI.COLUMNS.INTEREST_RATE) {
            // Interest Rate -> Principal Payment (same row)
            nextColIndex = EXISTING_DEBT_UI.COLUMNS.PRINCIPAL_PAYMENT;
          } else {
            // Principal Payment -> next row Interest Rate
            nextColIndex = EXISTING_DEBT_UI.COLUMNS.INTEREST_RATE;
            nextRowIndex++;
          }
        } else if (direction === 'prev' || direction === 'left') {
          // Move to previous column, or previous row if at first column
          if (nextColIndex > EXISTING_DEBT_UI.COLUMNS.INTEREST_RATE) {
            // Principal Payment -> Interest Rate
            nextColIndex = EXISTING_DEBT_UI.COLUMNS.INTEREST_RATE;
          } else {
            // Interest Rate -> previous row Principal Payment
            nextColIndex = EXISTING_DEBT_UI.COLUMNS.PRINCIPAL_PAYMENT;
            nextRowIndex--;
          }
        } else if (direction === 'up') {
          // Move to same column, previous row
          nextRowIndex--;
        } else if (direction === 'down') {
          // Move to same column, next row
          nextRowIndex++;
        }

        // Clamp to valid range
        nextRowIndex = Math.max(0, Math.min(nextRowIndex, values.payments.length - 1));
        nextColIndex = Math.max(
          EXISTING_DEBT_UI.COLUMNS.INTEREST_RATE,
          Math.min(nextColIndex, EXISTING_DEBT_UI.COLUMNS.PRINCIPAL_PAYMENT)
        );

        // Check if the target field is disabled or locked
        const targetPayment = values.payments[nextRowIndex];
        if (targetPayment) {
          const targetIsLocked = isPaymentDateLocked(targetPayment.paymentDate);
          // Skip locked dates (they're filtered out of display)
          if (targetIsLocked) {
            continue;
          }
          // If not disabled, we found a valid target
          if (!isDisabled) {
            break;
          }
        } else {
          // No payment at this index, stop
          break;
        }
      }

      // Focus the next input using requestAnimationFrame for better timing
      // Mark that we're navigating to prevent input's auto-select from conflicting
      isNavigatingRef.current = true;
      activeFocusRef.current = { rowIndex: nextRowIndex, colIndex: nextColIndex };

      const nextRef = getInputRef(nextRowIndex, nextColIndex);
      requestAnimationFrame(() => {
        nextRef.current?.focus();
        nextRef.current?.select();
        // Reset navigation flag after focus is established
        requestAnimationFrame(() => {
          isNavigatingRef.current = false;
        });
      });
    },
    [values.payments, getInputRef, isPaymentDateLocked, isDisabled]
  );

  /**
   * Keyboard navigation handler for table inputs
   *
   * Enables efficient data entry across payment schedule table:
   * - Enter/ArrowRight: Next field (Interest Rate → Principal → next row)
   * - ArrowLeft: Previous field (Principal → Interest Rate → prev row)
   * - ArrowUp/Down: Same column, different row
   *
   * Automatically skips locked dates (May/November filters).
   * Auto-selects content for quick replacement.
   *
   * Max attempts prevents infinite loops if all fields locked.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        navigateField(rowIndex, colIndex, 'next');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateField(rowIndex, colIndex, 'next');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateField(rowIndex, colIndex, 'prev');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigateField(rowIndex, colIndex, 'down');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateField(rowIndex, colIndex, 'up');
      }
    },
    [navigateField]
  );

  // Handle quick fill from predefined templates
  const handleQuickFill = useCallback(
    (template: ExistingDebtPaymentInput[]) => {
      if (values.payments.length === 0) {
        // If no payments exist, we need to generate the schedule first
        // Show a helpful message to the user
        toaster.create({
          title: 'Payment Schedule Required',
          description: 'Please generate a payment schedule first before using quick fill.',
          type: 'warning',
          duration: 5000,
        });
        return;
      }

      // Create a map of template data by payment date
      const templateMap = new Map<string, ExistingDebtPaymentInput>();
      template.forEach((payment) => {
        templateMap.set(payment.paymentDate, payment);
      });

      // Update payments that match template dates
      const updatedPayments = values.payments.map((payment) => {
        const templateData = templateMap.get(payment.paymentDate);
        if (templateData) {
          // Convert template data to form format
          // interestRate: 0.05 -> "5.00" (multiply by 100, format to 2 decimals)
          // principalPayment: 1610000 -> "1610000.00" (format to 2 decimals)
          return {
            ...payment,
            interestRate:
              templateData.interestRate > 0 ? (templateData.interestRate * 100).toFixed(3) : '',
            principalPayment:
              templateData.principalPayment > 0 ? templateData.principalPayment.toFixed(2) : '0.00',
          };
        }
        return payment;
      });

      onUpdate({
        ...values,
        payments: updatedPayments,
      });

      // Clear calculated interest payments when quick fill is used
      setCalculatedInterestPayments(new Map());
      setHasCalculatedReverseAmortization(false);
    },
    [values, onUpdate]
  );

  // Listen for dev utilities events to trigger quick fill
  useEffect(() => {
    const handleDevUtilitiesFill = (event: Event) => {
      const customEvent = event as CustomEvent<{ template: ExistingDebtPaymentInput[] }>;
      if (customEvent.detail?.template) {
        handleQuickFill(customEvent.detail.template);
      }
    };

    window.addEventListener('dev-utilities:fill-existing-debt', handleDevUtilitiesFill);
    return () => {
      window.removeEventListener('dev-utilities:fill-existing-debt', handleDevUtilitiesFill);
    };
  }, [handleQuickFill]);

  const handleNameChange = useCallback(
    (value: string) => {
      markTouched('name');
      onUpdate({
        ...values,
        name: value,
      });
    },
    [values, onUpdate, markTouched]
  );

  const handlePaymentChange = useCallback(
    (index: number, field: keyof ExistingDebtPaymentForm, value: string) => {
      const updatedPayments = [...values.payments];
      updatedPayments[index] = {
        ...updatedPayments[index],
        [field]: value,
      };
      // Don't clear calculations on change - they will be recalculated on blur
      // This prevents flickering/fiscal year totals going to zero while typing
      onUpdate({
        ...values,
        payments: updatedPayments,
      });
    },
    [values, onUpdate]
  );

  // Handle principal payment blur - multiply by 1000 (user enters in thousands)
  // Only if auto-multiply is enabled and result wouldn't exceed 20 million
  // Then auto-calculates entire table using reverse amortization
  const handlePrincipalPaymentBlur = useCallback(
    (index: number) => {
      // If auto-multiply is disabled, just auto-calculate entire table
      if (!autoMultiplyEnabled) {
        calculateReverseAmortizationLocal();
        return;
      }

      const payment = values.payments[index];
      if (!payment) {
        calculateReverseAmortizationLocal();
        return;
      }

      const currentValue = payment.principalPayment;
      if (!currentValue || currentValue.trim() === '') {
        // Auto-calculate even if value is empty
        calculateReverseAmortizationLocal();
        return;
      }

      // Parse the value (CurrencyInput may have formatting, so we need to clean it)
      // Remove currency symbols, commas, etc.
      const cleanedValue = currentValue.replace(/[^\d.-]/g, '');
      const numValue = parseFloat(cleanedValue);

      if (Number.isNaN(numValue) || numValue === 0) {
        // Auto-calculate even if value is 0
        calculateReverseAmortizationLocal();
        return;
      }

      // Check if multiplying by 1000 would exceed 20 million
      const multipliedValue = numValue * EXISTING_DEBT_VALIDATION.AUTO_MULTIPLY_FACTOR;

      if (multipliedValue > EXISTING_DEBT_VALIDATION.MAX_PRINCIPAL_VALUE) {
        // Don't multiply if it would exceed the limit, but still auto-calculate
        calculateReverseAmortizationLocal();
        return;
      }

      // Multiply by 1000 to convert from thousands to full amount
      const formattedValue = multipliedValue.toFixed(2);

      // Update the payment with the multiplied value
      const updatedPayments = [...values.payments];
      updatedPayments[index] = {
        ...updatedPayments[index],
        principalPayment: formattedValue,
      };

      // Update state
      setCalculatedInterestPayments(new Map());
      setHasCalculatedReverseAmortization(false);
      onUpdate({
        ...values,
        payments: updatedPayments,
      });

      // Auto-calculate entire table with updated payments
      // Calculate immediately after state update (no setTimeout for consistent timing)
      markTouched('payments');
      calculateReverseAmortizationLocal();
    },
    [autoMultiplyEnabled, values, onUpdate, calculateReverseAmortizationLocal, markTouched]
  );

  // Handle interest rate blur - auto-calculate entire table using reverse amortization
  const handleInterestRateBlur = useCallback(() => {
    markTouched('payments');
    calculateReverseAmortizationLocal();
  }, [calculateReverseAmortizationLocal, markTouched]);

  /**
   * Generate payment schedule from available date records
   *
   * Creates a payment entry for every date record up to the selected
   * final payment date. Preserves existing data to avoid losing user input.
   *
   * Behavior:
   * - Collects all payment dates up to final date
   * - Removes duplicates and sorts chronologically
   * - Preserves existing payment data by date
   * - Adds new empty rows for missing dates
   * - Removes rows for dates beyond final payment
   * - Generates unique ID if needed (for multi-project management)
   *
   * Use case: User selects "2030-11-01" as final payment
   * → Generates rows for all dates from first available to 2030-11-01
   * → Existing data for those dates is preserved
   */
  const generatePaymentSchedule = useCallback(() => {
    if (!finalPaymentDate || availablePaymentDates.length === 0) {
      return;
    }

    const finalDate = new Date(finalPaymentDate);
    const targetPaymentDates: string[] = [];

    // Collect all payment dates from all dateRecords up to the final payment date
    for (const dateRecord of availablePaymentDates) {
      for (const paymentDate of dateRecord.paymentDates) {
        const paymentDateObj = new Date(paymentDate);
        // Only include dates up to and including the final payment date
        if (paymentDateObj <= finalDate) {
          targetPaymentDates.push(paymentDate);
        }
      }
    }

    // Sort by payment date to ensure chronological order
    targetPaymentDates.sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    // Remove duplicates
    const uniqueTargetDates = targetPaymentDates.filter(
      (date, index, self) => index === self.indexOf(date)
    );

    // Create a map of existing payments by date for quick lookup
    const existingPaymentsByDate = new Map<string, ExistingDebtPaymentForm>();
    values.payments.forEach((payment) => {
      existingPaymentsByDate.set(payment.paymentDate, payment);
    });

    // Build merged payments array - preserve existing data, add new rows for missing dates
    const mergedPayments: ExistingDebtPaymentForm[] = uniqueTargetDates.map((paymentDate) => {
      // If we have existing data for this date, use it
      const existingPayment = existingPaymentsByDate.get(paymentDate);
      if (existingPayment) {
        return existingPayment;
      }
      // Otherwise, create a new empty payment
      return {
        paymentDate,
        principalPayment: '0.00',
        interestRate: '',
        interestPayment: '',
      };
    });

    // Generate ID if it doesn't exist
    const debtProjectId =
      values.id ||
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `debt-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);

    // Only update if the schedule actually changed or ID was generated
    const hasChanged =
      mergedPayments.length !== values.payments.length ||
      mergedPayments.some((p, i) => p.paymentDate !== values.payments[i]?.paymentDate) ||
      !values.id;

    if (hasChanged) {
      onUpdate({
        ...values,
        id: debtProjectId,
        payments: mergedPayments,
      });

      // Clear calculated interest payments when schedule is regenerated
      setCalculatedInterestPayments(new Map());
      // Reset reverse amortization flag when schedule is regenerated
      setHasCalculatedReverseAmortization(false);
    }
  }, [finalPaymentDate, availablePaymentDates, values, onUpdate]);

  // Clear all payment data
  const handleClearAll = useCallback(() => {
    onUpdate({
      ...values,
      payments: [],
    });
    setCalculatedInterestPayments(new Map());
    setHasCalculatedReverseAmortization(false);
    setShowClearAllDialog(false);
  }, [values, onUpdate]);

  // Calculate running totals for footer and display
  const runningTotals = useMemo(() => {
    let totalPrincipal = 0;
    let totalInterest = 0;

    values.payments.forEach((payment, index) => {
      // Parse numeric values properly from NumberInput
      // CurrencyInput stores as formatted string, need to clean it
      const principalStr = payment.principalPayment || '';
      const principal = parseFloat(principalStr.replace(/[^\d.-]/g, '')) || 0;

      totalPrincipal += principal;

      // Use calculated interest payment if available, otherwise calculate simple interest
      const calculatedInterest = calculatedInterestPayments.get(index);
      if (calculatedInterest !== undefined) {
        totalInterest += calculatedInterest;
      } else {
        // Fallback to simple annual interest calculation
        const rateStr = payment.interestRate || '';
        const ratePercent = parseFloat(rateStr) || 0;
        const rateDecimal = ratePercent / 100;
        totalInterest += principal * rateDecimal;
      }
    });

    return {
      totalPrincipal,
      totalInterest,
      totalPayment: totalPrincipal + totalInterest,
    };
  }, [values.payments, calculatedInterestPayments]);

  // Calculate fiscal year totals for each payment
  // Step 1: Reverse amortization (already done) - gives us principal interest, interest payment, total payment
  // Step 2: Calculate fiscal year totals top-down (chronological order)
  //         - Sum the two payments from the same tax year
  //         - Only show total on the last payment of each fiscal year
  const fiscalYearTotals = useMemo(() => {
    if (!dateInputs || availablePaymentDates.length === 0 || !hasCalculatedReverseAmortization) {
      return new Map<string, number>();
    }

    // Map to store fiscal year totals (fiscalYearEndDate -> total)
    const totalsByFiscalYear = new Map<string, number>();

    // Step 1: Sort payments chronologically (top-down order)
    const sortedPayments = values.payments
      .map((payment, index) => ({ payment, index }))
      .sort((a, b) => a.payment.paymentDate.localeCompare(b.payment.paymentDate));

    // Step 2: Group payments by fiscal year and calculate totals
    const paymentsByFiscalYear = new Map<
      string,
      Array<{ payment: ExistingDebtPaymentForm; index: number; totalPayment: number }>
    >();

    // First pass: Group payments by fiscal year and calculate total payment for each
    sortedPayments.forEach(({ payment, index }) => {
      // Find which dateRecord contains this payment date
      const matchingDateRecord = availablePaymentDates.find((record) =>
        record.paymentDates.includes(payment.paymentDate)
      );

      if (matchingDateRecord?.fiscalYearEndDate) {
        const fiscalYearEnd = matchingDateRecord.fiscalYearEndDate;

        // Calculate total payment using reverse amortization results
        const principalStr = payment.principalPayment || '';
        const principal = parseFloat(principalStr.replace(/[^\d.-]/g, '')) || 0;

        // Use calculated interest payment from reverse amortization
        const calculatedInterest = calculatedInterestPayments.get(index);
        const interestPayment = calculatedInterest !== undefined ? calculatedInterest : 0;
        const totalPayment = principal + interestPayment;

        // Group by fiscal year
        if (!paymentsByFiscalYear.has(fiscalYearEnd)) {
          paymentsByFiscalYear.set(fiscalYearEnd, []);
        }
        paymentsByFiscalYear.get(fiscalYearEnd)!.push({ payment, index, totalPayment });
      }
    });

    // Second pass: Calculate totals for each fiscal year (sum of two payments)
    // Only store totals for fiscal years that have payments
    paymentsByFiscalYear.forEach((payments, fiscalYearEnd) => {
      // Sum all payments in this fiscal year
      const fiscalYearTotal = payments.reduce((sum, p) => sum + p.totalPayment, 0);
      totalsByFiscalYear.set(fiscalYearEnd, fiscalYearTotal);
    });

    return totalsByFiscalYear;
  }, [
    values.payments,
    calculatedInterestPayments,
    availablePaymentDates,
    dateInputs,
    hasCalculatedReverseAmortization,
  ]);

  // Get column title based on tax collection schedule
  const fiscalYearColumnTitle = useMemo(() => {
    if (!dateInputs?.taxCollectionSchedule) return 'Fiscal Year Total';
    return dateInputs.taxCollectionSchedule === 'summer' ? 'Fiscal Year Total' : 'Levy Year Total';
  }, [dateInputs?.taxCollectionSchedule]);

  // Extract all unique payment dates from availablePaymentDates
  const allPaymentDates = useMemo(() => {
    const dates = new Set<string>();
    availablePaymentDates.forEach((record) => {
      record.paymentDates.forEach((date) => {
        dates.add(date);
      });
    });
    return Array.from(dates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  }, [availablePaymentDates]);

  // Create payment date options - just use date strings directly
  const paymentDateOptions = useMemo(() => {
    return allPaymentDates.map((date) => ({
      value: date,
      label: date,
    }));
  }, [allPaymentDates]);

  // Filter payment dates based on search query
  const filteredPaymentDateOptions = useMemo(() => {
    if (!paymentDateSearchQuery.trim()) {
      return paymentDateOptions;
    }
    const query = paymentDateSearchQuery.toLowerCase();
    return paymentDateOptions.filter((option) => {
      // Search in the date string
      return (
        option.value.toLowerCase().includes(query) || option.label.toLowerCase().includes(query)
      );
    });
  }, [paymentDateOptions, paymentDateSearchQuery]);

  // Convert form values to engine format for validation
  const convertToEngineFormatForValidation = useCallback(
    (formValues: ExistingDebtInputForm): ExistingDebtInput => {
      return {
        id: formValues.id,
        name: formValues.name,
        existingDebtPaymentInputs: (formValues.payments || []).map((payment) => {
          const principalPaymentStr = payment.principalPayment || '';
          const principalPayment = parseFloat(principalPaymentStr.replace(/[^\d.-]/g, '')) || 0;
          const interestRate = parseFloat(payment.interestRate) / 100 || 0;
          const savedInterestPayment = parseFloat(payment.interestPayment) || 0;

          return {
            paymentDate: payment.paymentDate,
            interestRate,
            principalPayment,
            principalInterest: savedInterestPayment > 0 ? savedInterestPayment : 0,
          };
        }),
      };
    },
    []
  );

  // Validation errors using builder validation (single source of truth)
  const validationErrors = useMemo(() => {
    // Use ExistingDebtBuilder.validateDebtInput() for consistency
    const engineFormat = convertToEngineFormatForValidation(values);
    const validation = ExistingDebtBuilder.validateDebtInput(engineFormat);
    const allErrors = validation.errors;

    // Show all errors if showValidation is true
    if (showValidation) {
      return allErrors;
    }

    // Otherwise, filter by touched fields
    const filteredErrors: string[] = [];

    for (const error of allErrors) {
      // Map errors to fields
      if (error.includes('Debt ID')) {
        if (touched.has('id')) {
          filteredErrors.push(error);
        }
      } else if (error.includes('Debt name')) {
        if (touched.has('name')) {
          filteredErrors.push(error);
        }
      } else if (error.includes('Payment')) {
        // Payment-level errors - show if any payment field is touched
        if (touched.has('payments')) {
          filteredErrors.push(error);
        }
      } else {
        // Unknown error - show if any field touched
        filteredErrors.push(error);
      }
    }

    return filteredErrors;
  }, [values, touched, showValidation, convertToEngineFormatForValidation]);

  // Check if all required fields touched
  const allFieldsTouched = useMemo(() => {
    return touched.has('id') && touched.has('name') && touched.has('payments');
  }, [touched]);

  return {
    handlers: {
      handleNameChange,
      handlePaymentChange,
      handlePrincipalPaymentBlur,
      handleInterestRateBlur,
      handleQuickFill,
      generatePaymentSchedule,
      handleClearAll,
      handleKeyDown,
      isPaymentDateLocked,
      getInputRef,
      setFinalPaymentDate,
      setPaymentDateSearchQuery,
      setAutoMultiplyEnabled,
      setLockMayDates,
      setLockNovemberDates,
      setShowClearAllDialog,
      markTouched,
      // Focus management
      handleInputFocus,
      handleInputBlur,
      isNavigating,
      restoreFocus,
    },
    computed: {
      calculatedInterestPayments,
      hasCalculatedReverseAmortization,
      fiscalYearTotals,
      fiscalYearColumnTitle,
      runningTotals,
      allPaymentDates,
      paymentDateOptions,
      filteredPaymentDateOptions,
      validationErrors,
      allFieldsTouched,
    },
    state: {
      finalPaymentDate,
      paymentDateSearchQuery,
      autoMultiplyEnabled,
      lockMayDates,
      lockNovemberDates,
      showClearAllDialog,
    },
    refs: {
      inputRefs,
    },
    constants: {
      MAX_PRINCIPAL_VALUE: EXISTING_DEBT_VALIDATION.MAX_PRINCIPAL_VALUE,
      AUTO_MULTIPLY_FACTOR: EXISTING_DEBT_VALIDATION.AUTO_MULTIPLY_FACTOR,
      MONTHS: EXISTING_DEBT_UI.MONTHS,
    },
  };
};
